import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../component/table";
import { getProdcutInventoryData } from "../../../../redux/actions/inventory";
import Loader from "react-spinner-loader";
import { isFloat, sanitizeInput } from "../../../../utils";
import { GET_PRODUCT_INVENTORY_DATA } from "../../../../redux/constants";
import moment from "moment";

function ProductInventoryTable({
  debouncedHandleSearch,
  debounceProductFilter,
  tableView,
  simulationModeOn,
  setPage,
  page,
}) {
  // Constant
  const columnsPerPage = 11;
  const columnList = [
    "CW 1",
    "CW 2",
    "CW 3",
    "CW 4",
    "CW 5",
    "CW 6",
    "CW 7",
    "CW 8",
    "CW 9",
  ];

  const collapsibleRows = [
    {
      name: "Opening Stock",
      value: "openingStock",
    },
    {
      name: "Production Orders (+)",
      value: "productionOrder",
    },
    {
      name: "Sales Order (-)",
      value: "salesOrder",
    },
    {
      name: "Correction Orders (+/-)",
      value: "correctionOrder",
    },
    {
      name: "Closing Stock",
      value: "closingStock",
    },
  ];

  // State
  const [tableLoader, setTableLoader] = useState(false);
  const [sort, setSort] = useState();
  const [collapse, setCollapse] = useState([]);
  const [currentTableView, setCurrentTableView] = useState();

  // Redux
  const dispatch = useDispatch();
  const { productInventoryData } = useSelector((state) => state.inventory);

  const getProductInventoryDataList = (productIdWithSequence) => {
    const request = {
      tableView,
      payload: {
        products: productIdWithSequence,
      },
      onSuccess: () => {
        setTableLoader(false);
      },
      onError: (err) => {
        setTableLoader(false);
      },
    };
    dispatch(getProdcutInventoryData(request));
  };

  useEffect(() => {
    switch (tableView.value) {
      case "week":
        setCurrentTableView("weekWise");
        break;
      case "day":
        setCurrentTableView("dayWise");
        break;
      case "month":
        setCurrentTableView("monthWise");
        break;
      default:
        setCurrentTableView("weekWise");
        break;
    }
    if (debounceProductFilter?.length > 0) {
      setTableLoader(true);
      const productIdWithSequence = debounceProductFilter.map((item, index) => {
        const obj = {
          product_master_id: item.value,
          sequence: index + 1,
        };
        return obj;
      });
      getProductInventoryDataList(productIdWithSequence);
    }
  }, [
    debouncedHandleSearch,
    debounceProductFilter,
    tableView,
    simulationModeOn,
  ]);

  const sortProducts = (a, b) => {
    const productA = a.product_name.toUpperCase();
    const productB = b.product_name.toUpperCase();

    if (sort.type === "asc") {
      return productA < productB ? -1 : productA > productB ? 1 : 0;
    } else if (sort.type === "desc") {
      return productA > productB ? -1 : productA < productB ? 1 : 0;
    }

    return 0;
  };

  useEffect(() => {
    if (sort) {
      const sortData = [...productInventoryData];
      sortData.sort(sortProducts);
      dispatch({
        type: GET_PRODUCT_INVENTORY_DATA,
        payload: sortData,
      });
    }
  }, [sort]);

  /* ** Creating function to toggle the collapse button */
  const toggleCollapse = (index) => {
    if (collapse.includes(index)) {
      setCollapse(collapse.filter((item) => item !== index));
    } else {
      setCollapse([...collapse, index]);
    }
  };

  const getStatus = (week_data, item) => {
    let status;
    if (week_data.closingStock < 0) {
      return (status = "danger");
    } else if (
      0 <= week_data.closingStock &&
      week_data.closingStock < item.min_qty
    ) {
      return (status = "warning");
    }
    return status;
  };

  // Function to show CW data
  const showCWData = (data, item) => {
    const status = getStatus(data, item);
    const closingValue = isFloat(data?.closingStock) ? data?.closingStock?.toFixed(2) : data?.closingStock;
    switch (status) {
      case "warning":
        return (
          <div className="px-1 py-0.5 border-2 border-[#F7921E] text-[#F7921E] text-center bg-white rounded-full">
            {closingValue}
          </div>
        );

      case "danger":
        return (
          <div className="px-1 py-0.5 text-white text-center bg-[#D31313] rounded-full">
            {closingValue}
          </div>
        );

      default:
        return closingValue;
    }
  };

  // Function to calculate entered value in simulation mode for "week wise", "day wise" and "month wise" view
  const calculateSimulationData = (
    entered_value,
    simulatedItem,
    field_name,
    productName
  ) => {
    const skuObject = productInventoryData.find(
      (item) => item.product_name === productName
    );

    if (skuObject) {
      switch (tableView.value) {
        case "week":
          skuObject.weekWise.forEach((element, index) => {
            if (element.weekName === simulatedItem.weekName) {
              element[field_name] = Number(entered_value);
            } else {
              if (index !== 0) {
                element.openingStock =
                  skuObject.weekWise[index - 1].closingStock;
              }
            }
            element.closingStock =
              element.openingStock -
              element.salesOrder +
              element.productionOrder +
              element.correctionOrder;
          });
          break;

        case "day":
          skuObject.dayWise.forEach((element, index) => {
            if (element.dayNumber === simulatedItem.dayNumber) {
              element[field_name] = Number(entered_value);
            } else {
              if (index !== 0) {
                element.openingStock =
                  skuObject.dayWise[index - 1].closingStock;
              }
            }
            element.closingStock =
              element.openingStock -
              element.salesOrder +
              element.productionOrder +
              element.correctionOrder;

            // console.log({ element });
          });
          break;

        case "month":
          skuObject.monthWise.forEach((element, index) => {
            if (
              `${element.monthNumber}, ${element.yearNumber}` ===
              `${simulatedItem.monthNumber}, ${simulatedItem.yearNumber}`
            ) {
              element[field_name] = Number(entered_value);
            } else {
              if (index !== 0) {
                element.openingStock =
                  skuObject.monthWise[index - 1].closingStock;
              }
            }
            element.closingStock =
              element.openingStock -
              element.salesOrder +
              element.productionOrder +
              element.correctionOrder;

            // console.log({ element });
          });
          break;

        default:
          break;
      }
    }

    dispatch({
      type: GET_PRODUCT_INVENTORY_DATA,
      payload: productInventoryData,
    });
  };

  // Function to get columns for weekWise
  const getWeekWiseColumnsData = () => {
    if (productInventoryData?.length > 0 && productInventoryData[0].weekWise) {
      const columnData = productInventoryData[0].weekWise
        .slice(1)
        .slice(
          (page - 1) * columnsPerPage,
          (page - 1) * columnsPerPage + columnsPerPage
        )
        .map((item) => {
          const obj = {
            headName: `Week ${item.weekNumber}`,
            isSort: false,
            value: `Week ${item.weekNumber}`,
          };
          return obj;
        });
      return columnData;
    } else {
      return [
        {
          headName: ``,
          isSort: false,
          value: ``,
        },
      ];
    }
  };

  // Function to get columns for daywise
  const getDayWiseColumnsData = () => {
    if (productInventoryData?.length > 0 && productInventoryData[0].dayWise) {
      const columnData = productInventoryData[0].dayWise
        .slice(1)
        .slice(
          (page - 1) * columnsPerPage,
          (page - 1) * columnsPerPage + columnsPerPage
        )
        .map((item) => {
          const obj = {
            headName: `${item.dayNumber} ${moment()
              .month(item.monthNumber)
              .format("MMM")}`,
            isSort: false,
            value: `${item.dayNumber} ${moment()
              .month(item.monthNumber)
              .format("MMM")}`,
          };
          return obj;
        });
      return columnData;
    } else {
      return [
        {
          headName: ``,
          isSort: false,
          value: ``,
        },
      ];
    }
  };

  // Function to get columns for monthWise
  const getMonthWiseColumnsData = () => {
    if (productInventoryData?.length > 0 && productInventoryData[0].monthWise) {
      const columnData = productInventoryData[0].monthWise
        .slice(1)
        .slice(
          (page - 1) * columnsPerPage,
          (page - 1) * columnsPerPage + columnsPerPage
        )
        .map((item) => {
          const obj = {
            headName: `${moment().month(item?.monthNumber).format("MMM")} ${
              item?.yearNumber
            }`,
            isSort: false,
            value: `${moment().month(item?.monthNumber).format("MMM")} ${
              item?.yearNumber
            }`,
          };
          return obj;
        });
      return columnData;
    } else {
      return [
        {
          headName: ``,
          isSort: false,
          value: ``,
        },
      ];
    }
  };

  // Function to show first column
  const showFirstColumn = () => {
    switch (tableView.value) {
      case "week": {
        return productInventoryData[0]?.weekWise
          ? `Current Week ${productInventoryData[0]?.weekWise[0]?.weekNumber}`
          : "Current Week";
      }

      case "day": {
        return productInventoryData[0]?.dayWise
          ? `Today, ${productInventoryData[0]?.dayWise[0]?.dayNumber} ${moment()
              .month(productInventoryData[0]?.dayWise[0]?.monthNumber)
              .format("MMM")}`
          : "Today";
      }

      case "month": {
        return productInventoryData[0]?.monthWise
          ? `Current Month, ${moment()
              .month(productInventoryData[0]?.monthWise[0]?.monthNumber)
              .format("MMM")} ${
              productInventoryData[0]?.monthWise[0]?.yearNumber
            }`
          : "Current Month";
      }
      default:
        break;
    }
  };

  // Function to get Columns
  const getTableColumns = () => {
    if (tableView.value === "week") {
      return getWeekWiseColumnsData();
    } else if (tableView.value === "day") {
      return getDayWiseColumnsData();
    } else if (tableView.value === "month") {
      return getMonthWiseColumnsData();
    }
  };

  // Function to "Enable" or "Disable" next button of column pagination
  const enableOrDisableNextButton = () => {
    if (tableView.value === "week") {
      return (
        page ===
        Math.ceil(productInventoryData[0]?.weekWise?.length / columnsPerPage)
      );
    } else if (tableView.value === "day") {
      return (
        page ===
        Math.ceil(productInventoryData[0]?.dayWise?.length / columnsPerPage)
      );
    } else if (tableView.value === "month") {
      return (
        page ===
        Math.ceil(productInventoryData[0]?.monthWise?.length / columnsPerPage)
      );
    }
  };

  return (
    <div>
      {!tableLoader ? (
        <Table
          heads={[
            {
              headName: "Product",
              isSort: true,
              value: "product_name",
            },
            {
              headName: showFirstColumn(),
              isSort: false,
              value: "name",
            },
            ...getTableColumns(),
            {
              headName: (
                <div className="space-x-2">
                  <span
                    className={`fa-solid fa-chevron-left ${
                      page === 1
                        ? "text-slate-300 pointer-events-none"
                        : "cursor-pointer text-[#9C9C9C]"
                    }  font-semibold`}
                    onClick={(e) => setPage(page - 1)}
                  ></span>
                  <span
                    className={`fa-solid fa-chevron-right ${
                      enableOrDisableNextButton()
                        ? "text-slate-300 pointer-events-none"
                        : "cursor-pointer text-[#9C9C9C]"
                    }  font-semibold`}
                    onClick={(e) => {
                      setPage(page + 1);
                    }}
                  ></span>
                </div>
              ),
              isSort: false,
              value: "Action",
            },
          ]}
          tClass="table-auto w-full border-separate border-spacing-y-2"
          hClass="text-sm leading-2 tracking-tight font-semibold text-[#1E293B] bg-[--bg-color-100]"
          bClass="text-sm"
          thClass={`px-2 py-3 whitespace-nowrap text-left font-semibold first:pl-4 first:rounded-l-md last:rounded-r-md max-w-1/4`}
          setSort={setSort}
          sort={sort}
        >
          {debounceProductFilter?.length > 0 ? (
            productInventoryData.length > 0 ? (
              productInventoryData.map((item, index) => {
                return (
                  <>
                    <tr key={index} className="bg-[--bg-color-50]">
                      <td
                        title={item.sku_name}
                        className="px-2 py-2 text-sm leading-4 tracking-tight first:pl-4 font-semibold text-[#1E293B] text-left rounded-l-md truncate bg-[--bg-color-50]"
                      >
                        {item.product_name}
                        <div className="space-x-1 text-[11px] text-[#1E293B] font-normal tracking-tight">
                          <span>{item.product_no}</span>
                          {item.product_no && <span>&#124;</span>}
                          <span>UOM: {item.uom_name}</span>
                        </div>
                      </td>
                      <td
                        className={`px-2 py-2 text-sm leading-4 tracking-tight text-[#1E293B] text-left truncate bg-[--bg-color-50]
                          font-bold
                        `}
                      >
                        {showCWData(item[currentTableView][0], item)}
                      </td>

                      {item[currentTableView]
                        .slice(1)
                        .slice(
                          (page - 1) * columnsPerPage,
                          (page - 1) * columnsPerPage + columnsPerPage
                        )
                        .map((week_data, index) => {
                          return (
                            <td
                              className={`px-2 py-2 text-sm leading-4 tracking-tight text-[#1E293B] text-left truncate bg-[--bg-color-50]`}
                            >
                              {showCWData(week_data, item)}
                            </td>
                          );
                        })}

                      <td className="px-2 py-2 text-sm leading-4 tracking-tight text-[#1E293B] text-left truncate rounded-r-md bg-[--bg-color-50]">
                        <span
                          className={`fa-solid fa-chevron-down transition-transform ${
                            collapse.includes(index) && "rotate-180"
                          } text-[#9C9C9C] cursor-pointer`}
                          onClick={(e) => toggleCollapse(index)}
                        ></span>
                      </td>
                    </tr>
                    {collapse.includes(index) &&
                      collapsibleRows.map((row, i) => (
                        <tr key={i} className="">
                          <td className="flex justify-end" title={row.name}>
                            <div
                              className={`px-2 py-2 text-sm leading-4 tracking-tight first:pl-4 ${
                                (i === 0 || i === collapsibleRows.length - 1) &&
                                "font-semibold"
                              } text-[#1E293B] text-left rounded-l-md truncate w-3/4 bg-[--bg-color-50]`}
                            >
                              {row.name}
                            </div>
                          </td>
                          <td
                            className={`px-2 text-sm leading-4 tracking-tight text-[#1E293B] text-left truncate bg-[--bg-color-50] ${
                              row.value === "openingStock" ||
                              row.value === "closingStock"
                                ? "font-semibold"
                                : ""
                            }`}
                          >
                            {simulationModeOn &&
                            !(i === 0 || i === collapsibleRows.length - 1) ? (
                              <input
                                type="text"
                                name={row.value}
                                defaultValue={
                                  item[currentTableView][0][row.value]
                                }
                                className="form-input w-full text-sm py-0.5"
                                onChange={(e) => {
                                  const value = sanitizeInput(e.target.value);
                                  calculateSimulationData(
                                    value,
                                    item[currentTableView][0],
                                    row.value,
                                    item.product_name
                                  );
                                }}
                              />
                            ) : (
                              isFloat(item[currentTableView][0][row.value]) ? item[currentTableView][0][row.value]?.toFixed(2) : item[currentTableView][0][row.value]
                            )}
                          </td>
                          {item[currentTableView]
                            .slice(1)
                            .slice(
                              (page - 1) * columnsPerPage,
                              (page - 1) * columnsPerPage + columnsPerPage
                            )
                            .map((week_data, weekIndex) => (
                              <td
                                className={`px-2 text-sm leading-4 tracking-tight text-[#1E293B] text-left truncate bg-[--bg-color-50] ${
                                  row.value === "openingStock" ||
                                  row.value === "closingStock"
                                    ? "font-semibold"
                                    : ""
                                }`}
                              >
                                {simulationModeOn &&
                                !(
                                  i === 0 || i === collapsibleRows.length - 1
                                ) ? (
                                  <input
                                    type="text"
                                    name={row.value}
                                    defaultValue={week_data[row.value]}
                                    className="form-input w-full text-sm py-0.5"
                                    onChange={(e) => {
                                      const value = sanitizeInput(
                                        e.target.value
                                      );
                                      calculateSimulationData(
                                        value,
                                        week_data,
                                        row.value,
                                        item.product_name
                                      );
                                    }}
                                  />
                                ) : (
                                  isFloat(week_data[row.value]) ? week_data[row.value]?.toFixed(2) : week_data[row.value]
                                )}
                              </td>
                            ))}

                          <td
                            className={`px-2 text-sm leading-4 tracking-tight text-[#1E293B] text-left truncate bg-[--bg-color-50] rounded-r-md`}
                          ></td>
                        </tr>
                      ))}
                  </>
                );
              })
            ) : (
              <tr className="px-2 py-3 whitespace-nowrap">
                <td
                  colSpan={columnList.length + 3}
                  className="text-center py-2 text-lg text-[#1E293B] tracking-tight leading-4"
                >
                  There are no records to display
                </td>
              </tr>
            )
          ) : (
            <tr className="px-2 py-3 whitespace-nowrap">
              <td
                colSpan={columnList.length + 3}
                className="text-center py-2 text-lg text-[#1E293B] tracking-tight leading-4"
              >
                Please Select Products
              </td>
            </tr>
          )}
        </Table>
      ) : (
        <div className="flex justify-center p-2 my-2 whitespace-nowrap w-full">
          <Loader show={tableLoader} />
        </div>
      )}
    </div>
  );
}

export default ProductInventoryTable;
